import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, CheckCircle2, CircleSlash, CreditCard, AlertTriangle } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { t as tr, tk } from "@/i18n";

export const ENRICH_STATUS = {
  found: { label: tk("Enriched"), icon: CheckCircle2, cls: "text-emerald-600", msg: tk("Contact details found") },
  not_found: { label: tk("Not found"), icon: CircleSlash, cls: "text-muted-foreground", msg: tk("No contact details found — credits refunded") },
  billing_required: { label: tk("Billing required"), icon: CreditCard, cls: "text-amber-600", msg: tk("Engine billing issue — credits refunded. An administrator must top up the engine account.") },
  failed: { label: tk("Failed"), icon: AlertTriangle, cls: "text-red-600", msg: tk("Enrichment failed — credits refunded") },
};

const KIND = {
  contact: { field: "enrichment", tariff: "enrich", label: tk("Email & phone"), title: tk("Find professional email and phone") },
  email: { field: "enrichment_email", tariff: "email", label: tk("Find email"), title: tk("Find a verified professional email") },
  phone: { field: "enrichment_phone", tariff: "phone", label: tk("Find phone"), title: tk("Find a phone number") },
};
let tariffsP;
const loadTariffs = () => (tariffsP ||= api.get("/ai/status").then((r) => r.data.tariffs).catch(() => { tariffsP = null; return {}; }));

export function EnrichButton({ candidateId, enrichment, onDone, testid, kind = "contact" }) {
  const { setUpgradeOpen, refresh } = useAuth();
  const { t } = useI18n();
  const k = KIND[kind];
  const [state, setState] = useState(enrichment);
  const [cost, setCost] = useState(null);
  const timer = useRef();
  const running = ["queued", "running"].includes(state?.status);
  useEffect(() => setState(enrichment), [enrichment]);
  useEffect(() => { loadTariffs().then((x) => setCost(x[k.tariff] ?? null)); }, [k.tariff]);
  useEffect(() => {
    if (!running) return;
    timer.current = setTimeout(async () => {
      const { data } = await api.get(`/candidates/${candidateId}`);
      const st = data[k.field];
      setState(st || { status: "queued" });
      if (st?.status === "completed") {
        const s = ENRICH_STATUS[st.contact_status];
        (st.contact_status === "found" ? toast.success : toast.info)(tr(s?.msg || "Enrichment finished"));
        refresh?.(); onDone?.(data);
      }
    }, 4000);
    return () => clearTimeout(timer.current);
  }, [state, running, candidateId]); // eslint-disable-line
  const start = async (e) => {
    e.stopPropagation();
    try { await api.post(`/ai/enrich/${candidateId}?kind=${kind}`); setState({ status: "queued" }); toast.info(t("Searching… ({{count}} credits, refunded if nothing is found)", { count: cost ?? "…" })); }
    catch (err) { if (["upgrade_required", "insufficient_credits"].includes(errorCode(err))) setUpgradeOpen(true); else toast.error(errorDetail(err)); }
  };
  const s = state?.status === "completed" ? ENRICH_STATUS[state.contact_status] : null;
  const Icon = running ? Loader2 : s?.icon || Sparkles;
  return (
    <button onClick={start} disabled={running} data-testid={testid || `enrich-${kind}-${candidateId}`} data-status={running ? "enriching" : state?.contact_status || "idle"}
      title={s ? `${t(s.msg)}${state.error_detail ? ` · ${state.error_detail}` : ""}` : `${t(k.title)} · ${t("{{count}} credits, refunded if nothing found", { count: cost ?? "…" })}`}
      className="h-7 px-2.5 rounded-md border text-xs font-medium flex items-center gap-1 hover:bg-muted transition-colors disabled:opacity-60">
      <Icon className={`w-3.5 h-3.5 ${running ? "animate-spin" : s?.cls || "text-primary"}`} />
      {running ? t("Enriching…") : s ? `${t(k.label)} · ${t(s.label)}` : t(k.label)}
      {!running && !s && <span className="font-mono text-[10px] text-muted-foreground" data-testid={`${testid || `enrich-${kind}-${candidateId}`}-cost`}>{t("{{count}} cr.", { count: cost ?? "…" })}</span>}
    </button>
  );
}
